import { comprimento, dot, normalizar, somar, subtrair, vetor, type Vetor3 } from '../geometria/vetor3'
import { yFaceInternaDoTeto, type AncoraOcupante, type PerfilVeiculo } from './perfilVeiculo'
import type { ProporcoesVitima } from './perfisVitima'

/**
 * Superfícies internas que um corpo sem retenção encontra dentro do habitáculo.
 *
 * Existe para que o ocupante sem cinto (P3.1) não atravesse o veículo: ele para
 * na primeira superfície que cruza, e o painel diz QUAL foi. As posições vêm do
 * mesmo `PerfilVeiculo`/`AncoraOcupante` que a cena desenha — nunca números
 * soltos — para que "onde a vítima bate" e "o que está desenhado ali" sejam a
 * mesma geometria.
 */
export type IdSuperficie =
  | 'volante'
  | 'painel'
  | 'paraBrisa'
  | 'lateral'
  | 'teto'
  | 'encostoDianteiro'
  | 'apoioCabeca'

export interface SuperficieInterna {
  id: IdSuperficie
  nome: string
  /** ponto de referência da superfície, em coordenadas do veículo */
  ponto: Vetor3
  /** normal apontando para dentro do habitáculo — de onde o corpo vem */
  normal: Vetor3
  /** distância lateral ao ponto ainda considerada a mesma peça, não um vizinho */
  raioAlcance: number
  /** o que o contato costuma significar, para o painel explicar ao aluno */
  leitura: string
}

/** Ancora dianteira do mesmo lado (ou a dianteira mais próxima), usada como referência do encosto à frente. */
function ancoraDianteiraDeReferencia(perfil: PerfilVeiculo, ancora: AncoraOcupante): AncoraOcupante {
  const dianteiras = perfil.ancoras.filter((a) => a.fila === 'dianteira')
  return dianteiras.find((a) => a.lado === ancora.lado) ?? dianteiras[0] ?? ancora
}

/**
 * Superfícies candidatas para ESTA posição de ocupante.
 *
 * Não depende do tipo de colisão: a mesma lista serve para as quatro, e é
 * `primeiroContato` — dirigido pela trajetória livre — quem decide qual delas
 * o corpo de fato encontra em cada caso.
 */
export function superficiesDoHabitaculo(
  perfil: PerfilVeiculo,
  ancora: AncoraOcupante,
): SuperficieInterna[] {
  const saida: SuperficieInterna[] = []
  const dianteira = ancora.fila === 'dianteira'

  // o próprio apoio de cabeça: o que limita a extensão numa colisão traseira,
  // em qualquer fila — cada âncora tem o seu
  saida.push({
    id: 'apoioCabeca',
    nome: 'Apoio de cabeça do próprio banco',
    ponto: vetor(ancora.posicao.x, ancora.alturaAssento + 0.5, ancora.posicao.z - 0.18),
    normal: vetor(0, 0, 1),
    raioAlcance: 0.3,
    leitura: 'Contato da cabeça com o próprio apoio — é o que limita a extensão cervical numa colisão traseira.',
  })

  if (dianteira) {
    if (ancora.id === 'motorista') {
      saida.push({
        id: 'volante',
        nome: 'Volante',
        ponto: vetor(ancora.posicao.x, perfil.cinturaY - 0.14, perfil.colunaA.zBase - 0.36),
        normal: vetor(0, 0, -1),
        raioAlcance: 0.3,
        leitura: 'Impacto de tórax/cabeça contra o volante — típico do motorista sem cinto numa colisão frontal.',
      })
    } else {
      saida.push({
        id: 'painel',
        nome: 'Painel',
        ponto: vetor(ancora.posicao.x, perfil.cinturaY - 0.16, perfil.colunaA.zBase - 0.16),
        normal: vetor(0, 0, -1),
        raioAlcance: 0.4,
        leitura: 'Impacto contra o painel — comum no passageiro dianteiro sem cinto.',
      })
    }
    saida.push({
      id: 'paraBrisa',
      nome: 'Para-brisa',
      ponto: vetor(ancora.posicao.x, perfil.cinturaY + 0.32, perfil.colunaA.zTopo),
      normal: normalizar(vetor(0, -0.35, -1)),
      raioAlcance: 0.55,
      leitura: 'Contato de cabeça com o para-brisa: o corpo foi projetado além do painel.',
    })
  } else {
    const referencia = ancoraDianteiraDeReferencia(perfil, ancora)
    saida.push({
      id: 'encostoDianteiro',
      nome: 'Encosto do banco da frente',
      ponto: vetor(ancora.posicao.x, ancora.alturaAssento + 0.4, referencia.posicao.z - 0.24),
      normal: vetor(0, 0, -1),
      raioAlcance: 0.4,
      leitura: 'Impacto contra o encosto do banco dianteiro — típico de ocupante traseiro sem cinto.',
    })
  }

  const sinalLateral = ancora.lado === 'esquerdo' ? -1 : ancora.lado === 'direito' ? 1 : 0
  if (sinalLateral !== 0) {
    saida.push({
      id: 'lateral',
      nome: 'Lateral / porta',
      ponto: vetor(sinalLateral * (perfil.largura / 2 - 0.05), ancora.alturaAssento + 0.3, ancora.posicao.z),
      normal: vetor(-sinalLateral, 0, 0),
      raioAlcance: 0.5,
      leitura: 'Impacto contra a porta/coluna lateral — o cinto de três pontos pouco limita este movimento.',
    })
  }

  // catch-all: mesmo sem colisão dedicada, é contra o teto que o corpo solto
  // (ou o ocupante sem cinto) esbarra num capotamento
  saida.push({
    id: 'teto',
    nome: 'Teto',
    ponto: vetor(ancora.posicao.x, yFaceInternaDoTeto(perfil), ancora.posicao.z),
    normal: vetor(0, -1, 0),
    raioAlcance: 0.6,
    leitura: 'Contato com o teto — o que acontece a quem não está retido durante um capotamento.',
  })

  return saida
}

/**
 * Ponto de referência (tronco/cabeça) usado para detectar contato de um
 * ocupante sentado na âncora — a mesma origem que a cena e o painel devem usar,
 * para que "onde o corpo bate" seja sempre a mesma pergunta.
 */
export function origemDeContatoPadrao(ancora: AncoraOcupante, proporcoes: ProporcoesVitima): Vetor3 {
  return vetor(
    ancora.posicao.x,
    ancora.posicao.y + proporcoes.alturaPelve / 2 + proporcoes.alturaTronco * 0.7,
    ancora.posicao.z,
  )
}

export interface AmostraDeslocamento {
  /** instante, na mesma escala 0..1 da timeline */
  t: number
  /** deslocamento acumulado desde `origem`, em coordenadas do veículo */
  deslocamento: Vetor3
}

export interface Contato {
  superficie: SuperficieInterna
  /** instante do contato, na mesma escala 0..1 da trajetória amostrada */
  tempo: number
}

/**
 * Primeira superfície que a trajetória amostrada atravessa, partindo de `origem`.
 *
 * Recebe uma SEQUÊNCIA de amostras (não um único vetor) porque a trajetória
 * livre (`deslocamentoLivre`) não é linear no tempo — é a integral de uma
 * velocidade relativa que muda por trecho. Amostrar e checar a travessia entre
 * pares consecutivos é o mesmo princípio que os rastros da cinemática já usam
 * (`amostrarIntervalo`), só que aqui para detectar cruzamento de plano em vez
 * de desenhar uma linha.
 */
export function primeiroContato(
  superficies: readonly SuperficieInterna[],
  origem: Vetor3,
  amostras: readonly AmostraDeslocamento[],
): Contato | null {
  let melhor: Contato | null = null

  for (const superficie of superficies) {
    const normal = normalizar(superficie.normal)
    if (amostras.length === 0) continue

    let anterior = amostras[0]!
    let distanciaAnterior = dot(subtrair(somar(origem, anterior.deslocamento), superficie.ponto), normal)

    for (let i = 1; i < amostras.length; i += 1) {
      const atual = amostras[i]!
      const posicaoAtual = somar(origem, atual.deslocamento)
      const distanciaAtual = dot(subtrair(posicaoAtual, superficie.ponto), normal)

      if (distanciaAnterior >= -1e-9 && distanciaAtual <= 1e-9) {
        const denominador = distanciaAnterior - distanciaAtual
        const fracao = Math.abs(denominador) < 1e-12 ? 0 : distanciaAnterior / denominador
        const tempo = anterior.t + (atual.t - anterior.t) * fracao
        const posicaoNoContato = somar(
          origem,
          vetor(
            anterior.deslocamento.x + (atual.deslocamento.x - anterior.deslocamento.x) * fracao,
            anterior.deslocamento.y + (atual.deslocamento.y - anterior.deslocamento.y) * fracao,
            anterior.deslocamento.z + (atual.deslocamento.z - anterior.deslocamento.z) * fracao,
          ),
        )
        if (comprimento(subtrair(posicaoNoContato, superficie.ponto)) <= superficie.raioAlcance) {
          if (!melhor || tempo < melhor.tempo) melhor = { superficie, tempo }
        }
        break
      }

      anterior = atual
      distanciaAnterior = distanciaAtual
    }
  }

  return melhor
}
